import { inject, Injectable } from '@angular/core';
import App from '@app/app';
import { downgradeInjectable } from '@angular/upgrade/static';
import { angularjs } from '@app/service/migration-helpers/angularjs.injection';
import { ComposableModalService } from '@app/components/modals/service/composable-modal.service';
import { filter } from 'rxjs';
import { StudentDetailsComponent } from '@app/components/shopping-cart/student-details/student-details.component';
import type { ShoppingCartSubject } from '@app/interface/subject/shopping-cart-subject.interface';
import { DeviceService } from '@app/service/DeviceService';
import { FeatureFlagService } from '@app/service/feature-flag.service';

@Injectable({
  providedIn: 'root'
})
export class StudentDetailService {
  private readonly composableModalService = inject(ComposableModalService);
  private readonly shoppingCartStore = inject(angularjs.ShoppingCartStore);
  private readonly subjectStore = inject(angularjs.SubjectStore);
  private readonly deviceService: DeviceService = inject(DeviceService);
  private readonly featureFlagService = inject(FeatureFlagService);

  private studentDetailMobileModalState: boolean = false;
  private mobileOnComplete?: () => void;
  private incompleteProfileSubjects: ReadonlyArray<ShoppingCartSubject> = [];

  public getStudentDetailMobileModalState(): boolean {
    return this.studentDetailMobileModalState;
  }

  public getMobileSubjects(): ReadonlyArray<ShoppingCartSubject> {
    return this.incompleteProfileSubjects;
  }

  public closeStudentDetailMobileModal(): void {
    this.mobileOnComplete = undefined;
    this.studentDetailMobileModalState = false;
    this.incompleteProfileSubjects = [];
  }

  public openStudentDetailModalWithCallback(onComplete: () => void): void {
    this.incompleteProfileSubjects = this.shoppingCartStore.getPreorderSubjects();
    if (this.deviceService.isMobile()) {
      this.mobileOnComplete = onComplete;
      this.studentDetailMobileModalState = true;
      return;
    }
    this.copyStudentDetailModal(onComplete);
  }

  public completeMobileSave(subjectsData: unknown): void {
    const callback = this.mobileOnComplete;
    if (!callback) {
      throw new Error('Student Details must be opened with a completion callback.');
    }

    this.mobileOnComplete = undefined;
    this.incompleteProfileSubjects = [];
    this.studentDetailMobileModalState = false;
    this.saveSubjects(subjectsData, callback);
  }

  public shouldOpenStudentDetailModal(): boolean {
    const featureFlag = this.featureFlagService.isEnabled('OPTIMIZEPREPIC');
    const hasEmptySubjects = this.shoppingCartStore.getPreorderSubjects();

    return featureFlag && hasEmptySubjects.length > 0;
  }

  private copyStudentDetailModal(onComplete: () => void): void {
    const dialogRef = this.composableModalService.openModal(
      {
        header: { titleKey: 'SUBMIT_DETAILS', icon: 'fa-sharp-regular-check-circle' },
        detail: {
          innerComponent: StudentDetailsComponent,
          componentInputs: { subjects: this.incompleteProfileSubjects }
        },
        buttons: {}
      },
      { width: '650px', panelClass: ['copy-order-modal'] }
    );

    dialogRef.closed.pipe(filter(Boolean)).subscribe((subjectsData): void => {
      this.saveSubjects(subjectsData, onComplete);
    });
  }

  private saveSubjects(subjectsData: unknown, onComplete: () => void): void {
    this.shoppingCartStore.updateSubjectNameBeforeCheckout(subjectsData);
    this.shoppingCartStore.applyOptionalPersonalizationFallbackBeforeCheckout();
    this.subjectStore.updateSubjectDetails(subjectsData);
    this.shoppingCartStore.deleteEmptyOrders();
    onComplete();
  }
}

App.factory('StudentDetailService', downgradeInjectable(StudentDetailService));
